import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import type {
  CreateNotification201,
  CreateNotificationBody,
  GetNotifications200,
  GetNotificationsParams,
  MarkAllNotificationsAsRead200,
  MarkAllNotificationsAsReadBody,
  MarkNotificationAsDelivered200,
  MarkNotificationAsFailed200,
  MarkNotificationAsFailedBody,
  MarkNotificationAsRead200
} from './types'


type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;



/**
 * @summary List notifications
 */
export const getNotifications = (
    params: GetNotificationsParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetNotifications200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/notifications`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetNotificationsQueryKey = (params: GetNotificationsParams,) => {
    return [`https://api.ugot.uk/api/notifications`, ...(params ? [params]: [])] as const;
    }

    
export const getGetNotificationsInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getNotifications>>, TError = AxiosError<void>>(params: GetNotificationsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNotificationsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNotifications>>> = ({ signal }) => getNotifications(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData> & { queryKey: QueryKey }
}

export type GetNotificationsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getNotifications>>>
export type GetNotificationsInfiniteQueryError = AxiosError<void>

/**
 * @summary List notifications
 */
export const useGetNotificationsInfinite = <TData = Awaited<ReturnType<typeof getNotifications>>, TError = AxiosError<void>>(
 params: GetNotificationsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetNotificationsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetNotificationsQueryOptions = <TData = Awaited<ReturnType<typeof getNotifications>>, TError = AxiosError<void>>(params: GetNotificationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNotificationsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNotifications>>> = ({ signal }) => getNotifications(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData> & { queryKey: QueryKey }
}

export type GetNotificationsQueryResult = NonNullable<Awaited<ReturnType<typeof getNotifications>>>
export type GetNotificationsQueryError = AxiosError<void>

/**
 * @summary List notifications
 */
export const useGetNotifications = <TData = Awaited<ReturnType<typeof getNotifications>>, TError = AxiosError<void>>(
 params: GetNotificationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetNotificationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * @summary Create notification
 */
export const createNotification = (
    createNotificationBody: CreateNotificationBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<CreateNotification201>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/notifications`,
      createNotificationBody,options
    );
  }



export const getCreateNotificationMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createNotification>>, TError,{data: CreateNotificationBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof createNotification>>, TError,{data: CreateNotificationBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createNotification>>, {data: CreateNotificationBody}> = (props) => {
          const {data} = props ?? {};

          return  createNotification(data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateNotificationMutationResult = NonNullable<Awaited<ReturnType<typeof createNotification>>>
    export type CreateNotificationMutationBody = CreateNotificationBody
    export type CreateNotificationMutationError = AxiosError<void>

    /**
 * @summary Create notification
 */
export const useCreateNotification = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createNotification>>, TError,{data: CreateNotificationBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof createNotification>>,
        TError,
        {data: CreateNotificationBody},
        TContext
      > => {

      const mutationOptions = getCreateNotificationMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary Mark notification as read
 */
export const markNotificationAsRead = (
    id: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<MarkNotificationAsRead200>> => {
    
    return axios.put(
      `https://api.ugot.uk/api/notifications/${id}/read`,undefined,options
    );
  }



export const getMarkNotificationAsReadMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markNotificationAsRead>>, TError,{id: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof markNotificationAsRead>>, TError,{id: string}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markNotificationAsRead>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  markNotificationAsRead(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type MarkNotificationAsReadMutationResult = NonNullable<Awaited<ReturnType<typeof markNotificationAsRead>>>
    
    export type MarkNotificationAsReadMutationError = AxiosError<void>

    /**
 * @summary Mark notification as read
 */
export const useMarkNotificationAsRead = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markNotificationAsRead>>, TError,{id: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof markNotificationAsRead>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getMarkNotificationAsReadMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary Mark notification as delivered
 */
export const markNotificationAsDelivered = (
    id: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<MarkNotificationAsDelivered200>> => {
    
    return axios.put(
      `https://api.ugot.uk/api/notifications/${id}/delivered`,undefined,options
    );
  }



export const getMarkNotificationAsDeliveredMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markNotificationAsDelivered>>, TError,{id: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof markNotificationAsDelivered>>, TError,{id: string}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markNotificationAsDelivered>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  markNotificationAsDelivered(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type MarkNotificationAsDeliveredMutationResult = NonNullable<Awaited<ReturnType<typeof markNotificationAsDelivered>>>
    
    export type MarkNotificationAsDeliveredMutationError = AxiosError<void>

    /**
 * @summary Mark notification as delivered
 */
export const useMarkNotificationAsDelivered = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markNotificationAsDelivered>>, TError,{id: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof markNotificationAsDelivered>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getMarkNotificationAsDeliveredMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary Mark notification as failed
 */
export const markNotificationAsFailed = (
    id: string,
    markNotificationAsFailedBody: MarkNotificationAsFailedBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<MarkNotificationAsFailed200>> => {
    
    return axios.put(
      `https://api.ugot.uk/api/notifications/${id}/failed`,
      markNotificationAsFailedBody,options
    );
  }



export const getMarkNotificationAsFailedMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markNotificationAsFailed>>, TError,{id: string;data: MarkNotificationAsFailedBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof markNotificationAsFailed>>, TError,{id: string;data: MarkNotificationAsFailedBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markNotificationAsFailed>>, {id: string;data: MarkNotificationAsFailedBody}> = (props) => {
          const {id,data} = props ?? {};

          return  markNotificationAsFailed(id,data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type MarkNotificationAsFailedMutationResult = NonNullable<Awaited<ReturnType<typeof markNotificationAsFailed>>>
    export type MarkNotificationAsFailedMutationBody = MarkNotificationAsFailedBody
    export type MarkNotificationAsFailedMutationError = AxiosError<void>

    /**
 * @summary Mark notification as failed
 */
export const useMarkNotificationAsFailed = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markNotificationAsFailed>>, TError,{id: string;data: MarkNotificationAsFailedBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof markNotificationAsFailed>>,
        TError,
        {id: string;data: MarkNotificationAsFailedBody},
        TContext
      > => {

      const mutationOptions = getMarkNotificationAsFailedMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary Delete notification
 */
export const deleteNotification = (
    id: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<void>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/notifications/${id}`,options
    );
  }



export const getDeleteNotificationMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNotification>>, TError,{id: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof deleteNotification>>, TError,{id: string}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteNotification>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  deleteNotification(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteNotificationMutationResult = NonNullable<Awaited<ReturnType<typeof deleteNotification>>>
    
    export type DeleteNotificationMutationError = AxiosError<void>

    /**
 * @summary Delete notification
 */
export const useDeleteNotification = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNotification>>, TError,{id: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof deleteNotification>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getDeleteNotificationMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary Mark all notifications as read
 */
export const markAllNotificationsAsRead = (
    markAllNotificationsAsReadBody: MarkAllNotificationsAsReadBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<MarkAllNotificationsAsRead200>> => {
    
    return axios.put(
      `https://api.ugot.uk/api/notifications/read-all`,
      markAllNotificationsAsReadBody,options
    );
  }



export const getMarkAllNotificationsAsReadMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markAllNotificationsAsRead>>, TError,{data: MarkAllNotificationsAsReadBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof markAllNotificationsAsRead>>, TError,{data: MarkAllNotificationsAsReadBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markAllNotificationsAsRead>>, {data: MarkAllNotificationsAsReadBody}> = (props) => {
          const {data} = props ?? {};

          return  markAllNotificationsAsRead(data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type MarkAllNotificationsAsReadMutationResult = NonNullable<Awaited<ReturnType<typeof markAllNotificationsAsRead>>>
    export type MarkAllNotificationsAsReadMutationBody = MarkAllNotificationsAsReadBody
    export type MarkAllNotificationsAsReadMutationError = AxiosError<void>

    /**
 * @summary Mark all notifications as read
 */
export const useMarkAllNotificationsAsRead = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markAllNotificationsAsRead>>, TError,{data: MarkAllNotificationsAsReadBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof markAllNotificationsAsRead>>,
        TError,
        {data: MarkAllNotificationsAsReadBody},
        TContext
      > => {

      const mutationOptions = getMarkAllNotificationsAsReadMutationOptions(options);

      return useMutation(mutationOptions);
    }
    